import React, { Fragment } from 'react'
import times from 'lodash.times'

import { FACES, ICON_BACKGROUNDS, DIE_TYPES, SKILLS } from '../../constants'
import advantage from '../../assets/advantage.png'
import success from '../../assets/success.png'
import threat from '../../assets/threat.png'
import failure from '../../assets/failure.png'
import triumph from '../../assets/triumph.png'
import despair from '../../assets/despair.png'
import { deleteRoll } from '../../api/rolling'
import EventCard from '../../components/EventCard'
import { computeResult } from './util'

const FaceIcon = ({ src }) => (
  <img className='face-icon' src={src} alt={src} />
)

const faceToContent = (face) => {
  switch (face) {
    case 's':
    case 'ss':
      return times(face.length).map((_, idx) => {
        return <FaceIcon src={success} key={idx} /> 
      })

    case 'sa':
      return (
        <>
          <FaceIcon src={success} />
          <FaceIcon src={advantage} />
        </>
      )

    case 'a':
    case 'aa':
      return times(face.length).map((_, idx) => {
        return <FaceIcon src={advantage} key={idx} /> 
      })

    case 'r':
      return <FaceIcon src={triumph} />

    case 'f':
    case 'ff':
      return times(face.length).map((_, idx) => {
        return <FaceIcon src={failure} key={idx} /> 
      })

    case 'ft':
      return (
        <>
          <FaceIcon src={failure} />
          <FaceIcon src={threat} />
        </>
      )

    case 't':
    case 'tt':
      return times(face.length).map((_, idx) => {
        return <FaceIcon src={threat} key={idx} /> 
      })

    case 'd':
      return <FaceIcon src={despair} />

    // Destiny dice
    case 'l':
    case 'll':
    case 'n':
    case 'nn':
      return times(face.length).map((_, idx) => {
        return (
          <div className='face-icon' key={`${face}-${idx}`} css={`
            margin-left: -1px;
            height: ${face.length === 2 ? '12' : '15'}px !important;
            width: ${face.length === 2 ? '12' : '15'}px !important;
            border-radius: 50%;
            border: 3px solid black;
            background-color: ${['n', 'nn'].includes(face) && 'black'};
          `} />
        )
      })

    default:
      return false
  }
}

const Pool = ({ diceType, rolls }) => {
  if (!Array.isArray(rolls) || rolls.length === 0) return false

  const backgroundInfo = ICON_BACKGROUNDS[diceType]
  const Component = backgroundInfo.component
  const isDiamond = [DIE_TYPES.ability, DIE_TYPES.difficulty].includes(diceType)
  const isSquare = [DIE_TYPES.boost, DIE_TYPES.setback].includes(diceType)

  return (
    <>
      {rolls.map((dieFace, idx) => {
        return (
          <div key={`${diceType}-${idx}`} css={`
            height: 50px;
            width: 50px;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            margin-right: 2px;

            #fill {
              fill: ${backgroundInfo.fill};
            }

            svg {
              transform: ${isDiamond && 'scaleX(1.5)'};
            }
          `}>
            <Component height={isSquare ? 45 : 50} width={isSquare ? 45 : 50} />
            <div css={`
              display: flex;
              flex-direction: column;
              position: absolute;

              .face-icon {
                height: auto;
                width: 15px;

                :only-child {
                  width: 20px;
                }

                :last-of-type:not(:only-child) {
                  margin-top: 2px;
                }
              }

              img {
                margin-left: -1px;
                filter: ${![DIE_TYPES.proficiency, DIE_TYPES.boost].includes(diceType) && 'invert(1)'};
              }
            `}>
              {faceToContent(FACES[diceType][dieFace].face)}
            </div>
          </div>
        )
      })}
    </>
  )
}

const Roll = ({ campaignId, created, description, displayName, id, isDarkMode, roll, skillIndex }) => {
  const [ loading, setLoading ] = React.useState(false)
  
  if (Object.keys(roll).length === 0) return false

  const { r, s, a, d, f, t, n, l } = computeResult(roll)
  const successResult = (s + r) - (f + d)
  const advResult = a - t

  const onDelete = async () => {
    setLoading(true)
    try {
      await deleteRoll(campaignId, id)
    } catch(e) {
      console.log(e)
      setLoading(false)
    }
  }

  const results = []

  if (successResult > 0) {
    let triumphs = r
    for (let i = 0; i < successResult; i++) {
      if (triumphs > 0) {
        triumphs--
        results.push('r')
      }
      else results.push('s')
    }
  } else {
    let despairs = d
    for (let i = 0; i < Math.abs(successResult); i++) {
      if (despairs > 0) {
        despairs--
        results.push('d')
      }
      else results.push('f')
    }
  }

  let bottomRight
  if (skillIndex !== false) {
    const skillName = Object.keys(SKILLS)[skillIndex]
    bottomRight = (
      <div css={`padding-left: 8px; margin-left: auto; text-transform: capitalize;`}>
        {skillName}
      </div>
    )
  }

  return (
    <EventCard
      campaignId={campaignId}
      created={created}
      description={description}
      bottomRight={bottomRight}
      displayName={displayName}
      id={id}
      isDarkMode={isDarkMode}
      loading={loading}
      setLoading={setLoading}
      onDelete={onDelete}
    >
      <div css={`
        padding: 6px;
        position: relative;
        overflow: hidden;
        background-color: ${n === 0 && l === 0 && !roll.d100 && successResult <= 0 && 'rgba(255, 0, 0, 0.075)'};
        background-color: ${d > 0 && 'rgba(255, 0, 0, 0.3)'};
      `}>
        <div css={`
          display: flex;
          justify-content: flex-end;
          align-items: flex-end;
          position: absolute;
          top: 0;
          bottom: 0;
          right: 0;
          opacity: 0.3;

          .bg {
            height: 100%;
            max-height: 90px;

            ${isDarkMode ? 'filter: invert(1);' : ''}
          }
        `}>
          {r > 0 &&
            <img src={triumph} alt='background' className='bg' />
          }
          {d > 0 &&
            <img src={despair} alt='background' className='bg' />
          }
        </div>
        <div css={`display: flex; flex-wrap: wrap;`}>
          <Pool diceType={DIE_TYPES.proficiency} rolls={roll[DIE_TYPES.proficiency]} />
          <Pool diceType={DIE_TYPES.ability} rolls={roll[DIE_TYPES.ability]} />
          <Pool diceType={DIE_TYPES.boost} rolls={roll[DIE_TYPES.boost]} />
          <Pool diceType={DIE_TYPES.challenge} rolls={roll[DIE_TYPES.challenge]} />
          <Pool diceType={DIE_TYPES.difficulty} rolls={roll[DIE_TYPES.difficulty]} />
          <Pool diceType={DIE_TYPES.setback} rolls={roll[DIE_TYPES.setback]} />
          <Pool diceType={DIE_TYPES.destiny} rolls={roll[DIE_TYPES.destiny]} />

          {/* D100 */}
          {roll.d100 && roll.d100.map((dice, idx) => {
            return (
              <div
                key={idx}
                css={`
                  margin-right: 2px;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  height: 48px;
                  width: 48px;
                  border: 1px solid black;
                  position: relative;
                  padding-top: 4px;

                  .num {
                    position: absolute;
                    top: 0px;
                    left: 2px;
                    font-size: 11px;
                  }
                `}
              >
                <div className='num'>d{dice.num}</div>
                {dice.roll}
              </div>
            )
          })}
        </div>

        {/* Not D100, not destiny, but results canceled out */}
        {results.length === 0 && Math.abs(advResult) === 0 && !roll.d100 && !roll[DIE_TYPES.destiny] &&
          <div css={`margin-top: 8px; text-align: left;`}>Dice canceled out</div>
        }

        {/* Normal results, or D100 */}
        {(results.length > 0 || Math.abs(advResult) > 0 || roll.d100) &&
          <div css={`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            margin-top: 8px;

            .face-icon {
              height: auto;
              width: 30px;
              margin-right: 4px;
              ${isDarkMode ? 'filter: invert(1)' : ''};
            }
          `}>
            {results.map((result, idx) => {
              return (
                <Fragment key={idx}>
                  {faceToContent(result)}
                </Fragment>
              )
            })}
            {times(Math.abs(advResult)).map((_, idx) => (
              <FaceIcon key={`adv-${idx}`} src={advResult > 0 ? advantage : threat} />
            ))}

            {/* D100, in case where there are multiple */}
            {roll.d100 && roll.d100.length > 1 &&
              <div css={`margin-right: 4px;`}>
                {roll.d100
                  .reduce((collector, { roll }) => {
                    collector.push(roll)
                    return collector
                  }, [])
                  .join(' + ')
                }
                {' '}=
              </div>
            }

            {/* Single D100 */}
            {roll.d100 &&
              <span css={`font-weight: bold;`}>
                {roll.d100.reduce((collector, { roll }) => {
                  return collector + roll
                }, 0)}
              </span>
            }
          </div>
        }
      </div>
    </EventCard>
  )
}

export default Roll
